import { ErrorOperation } from "namirasoft-core";
import { FormulaDefinitionRow } from "./FormulaDefinitionRow";
import { FormulaRow } from "./FormulaRow";
import { Formula_AmountByAverage } from "./statistical/amount/Formula_AmountByAverage";
import { Formula_AmountByLast } from "./statistical/amount/Formula_AmountByLast";
import { Formula_AmountByMax } from "./statistical/amount/Formula_AmountByMax";
import { Formula_AmountByMedian } from "./statistical/amount/Formula_AmountByMedian";
import { Formula_AmountByMin } from "./statistical/amount/Formula_AmountByMin";
import { Formula_InterquartileRange } from "./statistical/Formula_InterquartileRange";
import { Formula_PercentageByAverage } from "./statistical/percent/Formula_PercentageByAverage";
import { Formula_PercentageByLast } from "./statistical/percent/Formula_PercentageByLast";
import { Formula_PercentageByMax } from "./statistical/percent/Formula_PercentageByMax";
import { Formula_PercentageByMedian } from "./statistical/percent/Formula_PercentageByMedian";
import { Formula_PercentageByMin } from "./statistical/percent/Formula_PercentageByMin";
import { Formula_GrubbsTest } from "./statistical/z_score/Formula_GrubbsTest";
import { Formula_ModifiedZScore } from "./statistical/z_score/Formula_ModifiedZScore";
import { Formula_ZScore } from "./statistical/z_score/Formula_ZScore";
import { Formula_RegressionResidual } from "./trend/Formula_RegressionResidual";
import { Formula_TrendDeviation } from "./trend/Formula_TrendDeviation";
import { CalculationResult } from "./CalculationResult";
import { CostData } from "./CostData";

export class Formulas
{
    static ALL: FormulaDefinitionRow[] = [
        {
            name: "AmountByLast",
            parameters: {
                threshold: {
                    default: 2,
                    min: 0,
                    max: 10000
                }
            },
            description: "Flags anomaly if the difference from the last value exceeds the threshold",
            learn_more: "https://namirasoft.com/bill-watch/amount-by-last",
            formula: "Math.abs(difference) >= parameters.threshold",
            getCalculator: () => new Formula_AmountByLast()
        },
        {
            name: "AmountByMin",
            parameters: {
                threshold: {
                    default: 2,
                    min: 0,
                    max: 10000
                }
            },
            description: "Flags anomaly if the differnce from the minimum of previous values exceeds the threshold",
            learn_more: "https://namirasoft.com/bill-watch/amount-by-min",
            formula: "Math.abs(difference) >= parameters.threshold",
            getCalculator: () => new Formula_AmountByMin()
        },
        {
            name: "AmountByMax",
            parameters: {
                threshold: {
                    default: 2,
                    min: 0,
                    max: 10000
                }
            },
            description: "Flags anomaly if the differnce from the maximum of previous values exceeds the threshold",
            learn_more: "https://namirasoft.com/bill-watch/amount-by-max",
            formula: "Math.abs(difference) >= parameters.threshold",
            getCalculator: () => new Formula_AmountByMax()
        },
        {
            name: "AmountByAverage",
            parameters: {
                threshold: {
                    default: 2,
                    min: 0,
                    max: 10000
                }
            },
            description: "Flags anomaly if the differnce from the average of previous values exceeds the threshold",
            learn_more: "https://namirasoft.com/bill-watch/amount-by-average",
            formula: "Math.abs(difference) >= parameters.threshold",
            getCalculator: () => new Formula_AmountByAverage()
        },
        {
            name: "AmountByMedian",
            parameters: {
                threshold: {
                    default: 2,
                    min: 0,
                    max: 10000
                }
            },
            description: "Flags anomaly if the differnce from the median of previous values exceeds the threshold",
            learn_more: "https://namirasoft.com/bill-watch/amount-by-median",
            formula: "Math.abs(difference) >= parameters.threshold",
            getCalculator: () => new Formula_AmountByMedian()
        },
        {
            name: "PercentageByLast",
            parameters: {
                threshold: {
                    default: 5,
                    min: 0,
                    max: 100
                }
            },
            description: "Flags anomaly if the percentage change from the last value exceeds the threshold",
            learn_more: "https://namirasoft.com/bill-watch/percentage-by-last",
            formula: "Math.abs(percent) >= parameters.threshold",
            getCalculator: () => new Formula_PercentageByLast()
        },
        {
            name: "PercentageByMin",
            parameters: {
                threshold: {
                    default: 5,
                    min: 0,
                    max: 100
                }
            },
            description: "Flags anomaly if the percentage change from the minimum of previous values exceeds the threshold",
            learn_more: "https://namirasoft.com/bill-watch/percentage-by-min",
            formula: "Math.abs(percent) >= parameters.threshold",
            getCalculator: () => new Formula_PercentageByMin()
        },
        {
            name: "PercentageByMax",
            parameters: {
                threshold: {
                    default: 5,
                    min: 0,
                    max: 100
                }
            },
            description: "Flags anomaly if the percentage change from the maximum of previous values exceeds the threshold",
            learn_more: "https://namirasoft.com/bill-watch/percentage-by-max",
            formula: "Math.abs(percent) >= parameters.threshold",
            getCalculator: () => new Formula_PercentageByMax()
        },
        {
            name: "PercentageByAverage",
            parameters: {
                threshold: {
                    default: 5,
                    min: 0,
                    max: 100
                }
            },
            description: "Flags anomaly if the percentage change from the average of previous values exceeds the threshold",
            learn_more: "https://namirasoft.com/bill-watch/percentage-by-average",
            formula: "Math.abs(percent) >= parameters.threshold",
            getCalculator: () => new Formula_PercentageByAverage()
        },
        {
            name: "PercentageByMedian",
            parameters: {
                threshold: {
                    default: 5,
                    min: 0,
                    max: 100
                }
            },
            description: "Flags anomaly if the percentage change from the median of previous values exceeds the threshold",
            learn_more: "https://namirasoft.com/bill-watch/percentage-by-median",
            formula: "Math.abs(percent) >= parameters.threshold",
            getCalculator: () => new Formula_PercentageByMedian()
        },
        {
            name: "ZScore",
            parameters: {
                threshold: {
                    default: 2,
                    min: 0,
                    max: 5
                }
            },
            description: "Flags anomaly if a value's z-score exceeds the threshold, indicating it is far from the mean.",
            learn_more: "https://namirasoft.com/bill-watch/zscore",
            formula: "Math.abs(z_score) > parameters.threshold",
            getCalculator: () => new Formula_ZScore()
        },
        {
            name: "ModifiedZScore",
            parameters: {
                threshold: {
                    default: 3.5,
                    min: 1,
                    max: 5
                }
            },
            description: "Flags anomaly like z-score but uses the median and MAD",
            learn_more: "https://namirasoft.com/bill-watch/modified-zscore",
            formula: "Math.abs(modified_z_score) > parameters.threshold",
            getCalculator: () => new Formula_ModifiedZScore()
        },
        {
            name: "GrubbsTest",
            parameters: {
                rough_threshold: {
                    default: 0.05,
                    min: 0.001,
                    max: 0.1
                }
            },
            description: "A formal statistical test to determine whether the latest value is an outlier",
            learn_more: "https://namirasoft.com/bill-watch/grubbs-test",
            formula: "Math.abs(g) > critical_value",
            getCalculator: () => new Formula_GrubbsTest()
        },
        {
            name: "InterquartileRange",
            parameters: {
                multiplier: {
                    default: 1.5,
                    min: 1,
                    max: 3
                }
            },
            description: "Flags anomaly if a value falls outside the IQR range, scaled by a multiplier",
            learn_more: "https://namirasoft.com/bill-watch/interquartile-range",
            formula: "value < lower_bound || value > upper_bound",
            getCalculator: () => new Formula_InterquartileRange()
        },
        {
            name: "RegressionResidual",
            parameters: {
                threshold: {
                    default: 2,
                    min: 1,
                    max: 5
                }
            },
            description: "Flags anomaly based on how far the current value is from a linear regression prediction",
            learn_more: "https://namirasoft.com/bill-watch/regression-residual",
            formula: "Math.abs(z_score) > parameters.threshold",
            getCalculator: () => new Formula_RegressionResidual()
        },
        {
            name: "TrendDeviation",
            parameters: {
                threshold: {
                    default: 1,
                    min: 0.1,
                    max: 2
                }
            },
            description: "Flags anomaly if the value deviates from an estimated trend line beyond the threshold",
            learn_more: "https://namirasoft.com/bill-watch/trend-deviation",
            formula: "Math.abs(deviation) > parameters.threshold",
            getCalculator: () => new Formula_TrendDeviation()
        },
    ];
    static getFormulaDefinition(name: string): FormulaDefinitionRow
    {
        let fs = Formulas.ALL.filter(f => f.name.toLowerCase() === name.toLowerCase());
        if (fs.length === 0)
            ErrorOperation.throwHTTP(404, `Formula ${name} was not found`);
        return fs[0];
    }
    static evaluate(data: CostData[], value: CostData, formula: FormulaRow): CalculationResult
    {
        let f = Formulas.getFormulaDefinition(formula.name);
        let calculator = f.getCalculator();
        return calculator.evaluate(data, value, formula.change, formula.parameters);
    }
    static getDataValue<T>(all: T[], _default: T): { data: T[], value: T }
    {
        let value = all[all.length - 1] ?? _default;
        let data = all.slice(0, all.length - 1);
        return { data, value };
    }
}